import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Form from "~/common/components/Form";
import Input from "~/common/components/Input";
import useRequest from "~/common/hooks/useRequest";

import "~/assets/sass/pages/auth/login.scss";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/store";
import { API_STATE } from "~/common/constants/state";
import { login } from "~/store/auth/actions";

import { useTranslation } from "react-i18next";
import useAlert from "~/common/hooks/useAlert";

const LoginForm = () => {
	const loginForm = useForm();
	const navigate = useNavigate();
	const [showFailedLoginAlert, FailedLoginAlert] = useAlert();
	const [showProcessingAuthenticationAlert, ProcessingAuthenticationAlert] = useAlert();
	const [, dispatch] = useGlobalStore();
	const retryId = loginForm.watch().id;
	const loginRequest = useRequest("post", "vc/user/login");
	const registerRetryRequest = useRequest("post", `/vc/user/register/resend/${retryId}`);

	const { t } = useTranslation();

	const goJoinPage = () => {
		navigate("join");
	};

	const goFindPwPage = () => {
		navigate("findpw/find");
	};

	const emailFormValidation = (id) => {
		if (
			!id.match(
				/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i
			)
		) {
			loginForm.setError("id", {
				type: "pattern",
				message: t("pages.auth.LoginPage.component.LoginForm.includingAt"),
			});
			return false;
		} else {
			loginForm.clearErrors("id");
			return true;
		}
	};

	const submit = (data) => {
		if (!emailFormValidation(data.id)) {
			return;
		}
		loginRequest.call(data);
	};

	const activeLogin = () => {
		navigate("/dashboard");
	};

	const retryRequest = () => {
		registerRetryRequest.call(loginForm.watch());
	};

	useEffect(async () => {
		switch (loginRequest.state) {
			case API_STATE.done:
				dispatch(login({ accessToken: loginRequest.response.data.accessToken }));
				activeLogin();
				break;
			case API_STATE.error:
				switch (loginRequest.error.statusCode) {
					case 400:
						alert(t("pages.auth.LoginPage.component.LoginForm.dontMatchPw"));
						break;
					case 401:
						showProcessingAuthenticationAlert().then(() => retryRequest());
						break;
					case 404:
						showFailedLoginAlert();
						break;
					case 500:
					default:
						alert(t("pages.auth.LoginPage.component.LoginForm.retryInMoment"));
						break;
				}
				break;
			default:
				break;
		}
	}, [loginRequest.state, loginRequest.error, loginRequest.response, dispatch, navigate]);

	return (
		<>
			<h2 className="login-form-title">
				{t("pages.auth.LoginPage.component.LoginForm.login")}
			</h2>
			<Form useForm={loginForm} className="login-form vertical">
				<Input
					label="E-mail"
					placeholder={t("pages.auth.LoginPage.component.LoginForm.emailAddress")}
					name="id"
					type="email"
					validation={{
						required: t("pages.auth.LoginPage.component.LoginForm.enterEmail"),
					}}
					onEnter={loginForm.handleSubmit(submit)}
					required
				/>
				<Input
					label="Password"
					placeholder={t("pages.auth.LoginPage.component.LoginForm.pw")}
					name="pw"
					type="password"
					validation={{
						minLength: {
							value: 8,
							message: t("pages.auth.LoginPage.component.LoginForm.enterPw"),
						},
					}}
					onEnter={loginForm.handleSubmit(submit)}
					required
				/>
				<div className="login-form-bottom">
					<div className="login-option">
						<div className="checkbox">
							<label className={`checkbox-custom`}>
								<input type="checkbox" />
								<span className="checkmark" />
								<span className="checkbox-label">
									{t("pages.auth.LoginPage.component.LoginForm.staySigned")}
								</span>
							</label>
						</div>
						<button onClick={goFindPwPage} className="btn-pwd-find">
							{t("pages.auth.LoginPage.component.LoginForm.searchPw")}
						</button>
					</div>
					<button onClick={goJoinPage} className="btn-join">
						{t("pages.auth.LoginPage.component.LoginForm.joinMember")}
					</button>
				</div>
				<button
					type="button"
					onClick={loginForm.handleSubmit(submit)}
					className="btn__solid login-btn"
					color="primary"
				>
					{t("pages.auth.LoginPage.component.LoginForm.login")}
				</button>
			</Form>
			<FailedLoginAlert
				type="warning"
				message={`가입되지 않은 계정입니다.\n아이디를 확인하거나 관리자에게 문의해주세요.`}
				confirm="확인"
			/>
			<ProcessingAuthenticationAlert
				type="warning"
				message={`이메일 인증을 완료해주세요.\n이메일 인증을 완료하셔야 로그인이 가능합니다.`}
				confirm="재전송"
				reject="취소"
			/>
		</>
	);
};

export default LoginForm;
